import React, { createContext, useContext, useEffect, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { firestore } from "../../../configs/FirebaseConfig";
import { FIRESTORE_REGIONS_TABLE } from "../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../constants/CompanyConstants";
import { message } from "antd";

// Create context
const UseProducts = createContext();

// Provider component
export const ProductsProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);
  const [listener, setListener] = useState(null);

  // Set up listener when the provider mounts
  useEffect(() => {
    setupListener();

    // Cleanup listener when unmounting
    return () => {
      if (listener) {
        listener();
      }
    };
  }, []);

  const setupListener = () => {
    setIsLoading(true);
    setError(null);

    try {
      const productsQuery = collection(firestore, `${FIRESTORE_REGIONS_TABLE}/${COMPANY_COUNTRY_CODE}/products`);

      const unsubscribe = onSnapshot(
        productsQuery,
        (snapshot) => {
          try {
            const productsData = snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
            }));
            setProducts(productsData);
            setError(null);
            setIsLoading(false);
          } catch (err) {
            console.error("Error processing products snapshot:", err);
            setError(err.message);
            setIsLoading(false);
            message.error("Failed to process products data");
          }
        },
        (error) => {
          console.error("Error listening to products:", error);
          setError(error.message);
          setIsLoading(false);
          message.error("Failed to fetch products data");
        },
      );

      setListener(() => unsubscribe);
    } catch (error) {
      console.error("Error setting up products listener:", error);
      setError(error.message);
      setIsLoading(false);
      message.error("Failed to setup products listener");
    }
  };

  // Helper functions
  const getProductById = (id) => {
    return products.find((product) => product.id === id || product.productId === id);
  };

  const getProductByBarcode = (barcode) => {
    return products.find((product) => product.barcode === barcode);
  };

  const getProductsByCategory = (category) => {
    return products.filter((product) => product.category?.en === category);
  };

  const getInSeasonProducts = () => {
    return products.filter((product) => product.productInSeason === true);
  };

  const searchProducts = (searchTerm) => {
    const term = searchTerm.toLowerCase();
    return products.filter(
      (product) =>
        product.productName?.toLowerCase().includes(term) || product.category?.en?.toLowerCase().includes(term),
    );
  };

  return (
    <UseProducts.Provider
      value={{
        isLoading,
        products,
        error,
        getProductById,
        getProductByBarcode,
        getProductsByCategory,
        getInSeasonProducts,
        searchProducts,
      }}
    >
      {children}
    </UseProducts.Provider>
  );
};

// Custom hook to use the products context
export const useProducts = () => {
  const context = useContext(UseProducts);

  if (context === undefined) {
    throw new Error("useProducts must be used within a ProductsProvider");
  }

  return context;
};
